// 週排程編輯器（SPEC #ls2505-334「排程操作」、DEV-06、W22）：kinio_motion.schedule 等週排程屬性（7 × 48 字串）。
// - 列 = 週（顯示依語系的週首日，寫出仍是週日起）× 48 個半小時格；拖曳塗格（起點那格的反相決定這一筆是塗或擦）
// - 範本：全天 / 夜間（18:00–06:00）/ 上班時間（週一到週五 09:00–18:00）/ 清除，一鍵套
// - 顯示設備時區；儲存一次寫整個值；收斂後以 reported 重畫；有未存改動時離開要確認
// codec 在 @kinio/web-sdk schedule。data-testid：zed_schedule_editor、sch_cell_<day>_<slot>（data-on）、sch_tpl_<template>、zed_save、zed_close、zed_notice。
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SCHEDULE_SLOTS,
  emptySchedule,
  encodeSchedule,
  paintRect,
  parseSchedule,
  scheduleDayOrder,
  scheduleEqual,
  scheduleTemplate,
  slotLabel,
  type ScheduleGrid,
  type ScheduleTemplate,
} from '@kinio/web-sdk';
import { useConfirm } from '../../../components/ConfirmDialog';
import './zed.css';

export interface ScheduleSaveResult {
  error: string | null;
  reported?: unknown;
}

export interface ScheduleEditorProps {
  title: string;
  reported: unknown;
  /** 設備時區（IANA）；沒有就不顯示 */
  timeZone?: string;
  canSave: boolean;
  readOnlyReason?: string;
  onSave: (wire: string[]) => Promise<ScheduleSaveResult>;
  onClose: () => void;
}

const TEMPLATES: ScheduleTemplate[] = ['all_day', 'night', 'office', 'clear'];
const SUNDAY = new Date(Date.UTC(2026, 0, 4)); // 2026-01-04 是週日

/** 語系的週首日（Intl.Locale weekInfo；拿不到時：en-US 週日、其他週一）。 */
function weekStartOf(lang: string): number {
  try {
    const loc = new Intl.Locale(lang) as Intl.Locale & { weekInfo?: { firstDay: number }; getWeekInfo?: () => { firstDay: number } };
    const wi = loc.getWeekInfo?.() ?? loc.weekInfo;
    if (wi?.firstDay) return wi.firstDay % 7; // 1 = 週一 … 7 = 週日
  } catch {
    /* 舊瀏覽器 */
  }
  return lang.startsWith('en') ? 0 : 1;
}

export function ScheduleEditor({ title, reported, timeZone, canSave, readOnlyReason, onSave, onClose }: ScheduleEditorProps) {
  const { t, i18n } = useTranslation();
  const { ask, dialog } = useConfirm();
  const propBase = useMemo(() => parseSchedule(reported) ?? emptySchedule(), [reported]);
  const baseKey = JSON.stringify(encodeSchedule(propBase));
  // 儲存收斂後的設備回報：設定查詢重抓回來之前先當基準（不然剛存完 dirty 還是 true）
  const [saved, setSaved] = useState<ScheduleGrid | null>(null);
  const base = saved ?? propBase;
  const baseRef = useRef(base);
  const [grid, setGrid] = useState<ScheduleGrid>(base);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'saved' | 'reported' | 'error'; text?: string } | null>(null);
  const drag = useRef<{ base: ScheduleGrid; from: { day: number; slot: number } } | null>(null);
  const savingRef = useRef(false);
  const alive = useRef(true);
  const editable = canSave && !saving;
  const dirty = !scheduleEqual(grid, base);
  const order = scheduleDayOrder(weekStartOf(i18n.language));
  const dayName = (d: number) => new Intl.DateTimeFormat(i18n.language, { weekday: 'short', timeZone: 'UTC' }).format(new Date(SUNDAY.getTime() + d * 86_400_000));

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    const prev = baseRef.current;
    baseRef.current = propBase;
    setSaved(null);
    if (savingRef.current) return;
    setGrid((g) => (scheduleEqual(g, prev) ? propBase : g));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- baseKey 代表值
  }, [baseKey]);

  const requestClose = useCallback(async () => {
    if (savingRef.current) return;
    if (!dirty) return onClose();
    if (await ask({ danger: true, title: t('zed.discard_title'), body: t('zed.discard_body_schedule'), action: t('zed.discard') })) onClose();
  }, [dirty, onClose, ask, t]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || document.querySelector('[data-testid="confirm-dialog"]')) return;
      e.preventDefault();
      void requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [requestClose]);

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const cellOf = (e: { clientX: number; clientY: number }) => {
    const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
    const c = el?.closest('[data-cell]') as HTMLElement | null;
    if (!c) return null;
    const [day, slot] = (c.dataset.cell ?? '').split('_').map(Number);
    return Number.isInteger(day) && Number.isInteger(slot) ? { day, slot } : null;
  };
  const onDown = (e: ReactPointerEvent) => {
    if (!editable || (e.pointerType === 'mouse' && e.button !== 0)) return;
    const c = cellOf(e);
    if (!c) return;
    e.preventDefault();
    drag.current = { base: grid, from: c };
    setGrid(paintRect(grid, c, c));
    setNotice(null);
  };
  const onMove = (e: ReactPointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const c = cellOf(e);
    if (c) setGrid(paintRect(d.base, d.from, c));
  };
  const onUp = () => {
    drag.current = null;
  };

  const save = async () => {
    if (!canSave || !dirty || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setNotice(null);
    const sent = encodeSchedule(grid);
    let res: ScheduleSaveResult;
    try {
      res = await onSave(sent);
    } catch (e) {
      res = { error: e instanceof Error ? e.message : String(e) };
    }
    savingRef.current = false;
    if (!alive.current) return;
    setSaving(false);
    if (res.error) {
      setGrid(baseRef.current);
      setNotice({ kind: 'error', text: res.error });
      return;
    }
    const got = res.reported === undefined ? parseSchedule(sent)! : (parseSchedule(res.reported) ?? baseRef.current);
    setGrid(got);
    setSaved(got);
    baseRef.current = got;
    setNotice({ kind: scheduleEqual(got, parseSchedule(sent)!) ? 'saved' : 'reported' });
  };

  return (
    <div className="zed-overlay" role="dialog" aria-modal="true" aria-label={title} data-testid="zed_schedule_editor">
      <header className="zed-bar">
        <button type="button" className="btn" onClick={() => void requestClose()} disabled={saving} data-testid="zed_close">
          {t('zed.back')}
        </button>
        <h2 className="zed-title">{title}</h2>
        <div className="zed-actions">
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={!canSave || !dirty || saving} data-testid="zed_save">
            {saving ? t('zed.saving') : t('common.save')}
          </button>
        </div>
      </header>
      <div className="zed-modes">
        {TEMPLATES.map((tp) => (
          <button
            key={tp}
            type="button"
            className="btn btn-sm"
            disabled={!editable}
            onClick={() => {
              setGrid(scheduleTemplate(tp));
              setNotice(null);
            }}
            data-testid={`sch_tpl_${tp}`}
          >
            {t(`zed.tpl_${tp}`)}
          </button>
        ))}
        {timeZone && <span className="muted small">{t('zed.device_tz', { tz: timeZone })}</span>}
      </div>
      <div className="zed-sched-wrap">
        <div
          className={`zed-sched${editable ? '' : ' zed-readonly'}`}
          role="grid"
          aria-label={title}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onPointerLeave={onUp}
        >
          <div className="zed-sched-row zed-sched-head" role="row">
            <span className="zed-sched-day" />
            {Array.from({ length: SCHEDULE_SLOTS / 2 }, (_, h) => (
              <span key={h} className="zed-sched-hour" role="columnheader">
                {h % 3 === 0 ? slotLabel(h * 2).slice(0, 2) : ''}
              </span>
            ))}
          </div>
          {order.map((d) => (
            <div key={d} className="zed-sched-row" role="row">
              <span className="zed-sched-day" role="rowheader">
                {dayName(d)}
              </span>
              {grid[d].map((on, s) => (
                <span
                  key={s}
                  role="gridcell"
                  aria-selected={on}
                  aria-label={`${dayName(d)} ${slotLabel(s)}`}
                  className={`zed-cell${on ? ' on' : ''}${s % 2 === 1 ? ' half' : ''}`}
                  data-cell={`${d}_${s}`}
                  data-on={on ? '1' : '0'}
                  data-testid={`sch_cell_${d}_${s}`}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
      <footer className="zed-foot">
        {!canSave ? (
          <span className="muted" role="status" data-testid="zed_readonly">
            {readOnlyReason ?? t('zed.read_only_schedule')}
          </span>
        ) : notice ? (
          <span className={notice.kind === 'error' ? 'error' : notice.kind === 'reported' ? 'warn' : 'muted'} role="status" data-testid="zed_notice" data-kind={notice.kind}>
            {notice.kind === 'error' ? `${notice.text} ${t('zed.reported_updated')}` : notice.kind === 'saved' ? t('zed.saved') : t('zed.reported_updated')}
          </span>
        ) : (
          <span className="muted">{t('zed.hint_schedule')}</span>
        )}
      </footer>
      {dialog}
    </div>
  );
}
